/**
 * A storefront's own words for its nouns: "pack" for box, "card" for item, "duel" for battle,
 * "credit note" for voucher, "workshop" for the Forge.
 * Presentation only, like the currency — no core function reads it and no stored value changes
 * with it. Stored as overrides (`storefront_configs.terminology`, `{}` = every default), so an
 * unset term follows the default here if the default ever changes.
 *
 * A term is written as it reads mid-sentence ("pack", not "Pack"); the storefront capitalizes it
 * where copy starts a title or a sentence. Copy never puts "a" or "an" before a term, because the
 * right article depends on the word.
 */
import { z } from "zod";

export const STOREFRONT_TERM_KEYS = [
  "box",
  "item",
  "inventory",
  "battle",
  "voucher",
  "forge",
] as const;
export type StorefrontTermKey = (typeof STOREFRONT_TERM_KEYS)[number];

export interface StorefrontTerm {
  singular: string;
  plural: string;
}

/** Each term's default, with the dashboard's label and example for it. */
export const STOREFRONT_TERMS: Record<
  StorefrontTermKey,
  { label: string; hint: string; default: StorefrontTerm }
> = {
  box: {
    label: "Box",
    hint: "What shoppers buy and open: a pack, a case, a crate.",
    default: { singular: "box", plural: "boxes" },
  },
  item: {
    label: "Item",
    hint: "What an open wins: a card, a skin, a prize.",
    default: { singular: "item", plural: "items" },
  },
  inventory: {
    label: "Inventory",
    hint: "Where a shopper's items are kept: a collection, a vault.",
    default: { singular: "inventory", plural: "inventories" },
  },
  battle: {
    label: "Battle",
    hint: "Shoppers opening against each other: a duel, a showdown.",
    default: { singular: "battle", plural: "battles" },
  },
  voucher: {
    label: "Voucher",
    hint: "What replaces an item that could not be shipped: a credit note, a token.",
    default: { singular: "voucher", plural: "vouchers" },
  },
  forge: {
    label: "Forge",
    hint: "Where shoppers build boxes of their own: a workshop, a studio.",
    default: { singular: "forge", plural: "forges" },
  },
};

const word = z.string().trim().min(1).max(40);

/** One stored override: the singular, and the plural when the tenant gave one. */
export const storefrontTermOverrideSchema = z.object({
  singular: word,
  /** Absent means `${singular}s`. */
  plural: word.optional(),
});
export type StorefrontTermOverride = z.infer<typeof storefrontTermOverrideSchema>;

/** The stored shape: an override for each term the tenant renamed, and no others. */
export const storefrontTerminologyOverridesSchema = z.partialRecord(
  z.enum(STOREFRONT_TERM_KEYS),
  storefrontTermOverrideSchema,
);
export type StorefrontTerminologyOverrides = z.infer<typeof storefrontTerminologyOverridesSchema>;

const resolvedTermSchema = z.object({ singular: z.string().min(1), plural: z.string().min(1) });

/** The wire shape: every term, resolved. */
export const storefrontTerminologySchema = z.object({
  box: resolvedTermSchema,
  item: resolvedTermSchema,
  inventory: resolvedTermSchema,
  battle: resolvedTermSchema,
  voucher: resolvedTermSchema,
  forge: resolvedTermSchema,
} satisfies Record<StorefrontTermKey, typeof resolvedTermSchema>);
export type StorefrontTerminology = z.infer<typeof storefrontTerminologySchema>;

/** The plural a term takes when the tenant gave none: `${singular}s`, or the default's. */
export function impliedPlural(key: StorefrontTermKey, singular: string): string {
  return singular ? `${singular}s` : STOREFRONT_TERMS[key].default.plural;
}

function resolveTerm(key: StorefrontTermKey, override: StorefrontTermOverride | undefined) {
  if (!override) return STOREFRONT_TERMS[key].default;
  return {
    singular: override.singular,
    plural: override.plural || impliedPlural(key, override.singular),
  };
}

/** Stored overrides → every term, a default wherever the tenant renamed nothing. */
export function resolveStorefrontTerminology(
  overrides: StorefrontTerminologyOverrides,
): StorefrontTerminology {
  return {
    box: resolveTerm("box", overrides.box),
    item: resolveTerm("item", overrides.item),
    inventory: resolveTerm("inventory", overrides.inventory),
    battle: resolveTerm("battle", overrides.battle),
    voucher: resolveTerm("voucher", overrides.voucher),
    forge: resolveTerm("forge", overrides.forge),
  };
}

/**
 * The first path segments the storefront's other pages, its BFF (`/api`, `/health`) and its
 * static files hold, which the Forge's path never takes. A storefront test keeps it in step with
 * the route tree.
 */
export const RESERVED_FORGE_PATH_SEGMENTS: readonly string[] = [
  "api",
  "assets",
  "domain-connect",
  "health",
  "boxes",
  "battles",
  "account",
  "inventory",
  "fairness",
  "login",
  "terms",
  "privacy",
];

/** The Forge's path when the store's word for it cannot be one. */
export const DEFAULT_FORGE_PATH_SEGMENT = "forge";

const FORGE_PATH_SEGMENT_MAX = 40;

/**
 * Why a store's word for the Forge did not become its path: it has no letter a URL keeps
 * ("工房"), or its path is another page's ("Boxes").
 */
export type ForgePathFallback = "no_url_letters" | "reserved";

/**
 * The first segment of the Forge's storefront pages, from the store's singular for it: "Workshop"
 * → `workshop`, "Pack Lab" → `pack-lab`, "Atelier créatif" → `atelier-creatif`. The one place a
 * term reaches a URL. `forge`, naming the reason, when the word cannot be a path.
 */
export function resolveForgePath(singular: string): {
  segment: string;
  fallback: ForgePathFallback | null;
} {
  const slug = singular
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, FORGE_PATH_SEGMENT_MAX)
    .replace(/^-+|-+$/g, "");
  if (!slug) return { segment: DEFAULT_FORGE_PATH_SEGMENT, fallback: "no_url_letters" };
  if (RESERVED_FORGE_PATH_SEGMENTS.includes(slug)) {
    return { segment: DEFAULT_FORGE_PATH_SEGMENT, fallback: "reserved" };
  }
  return { segment: slug, fallback: null };
}

/** The Forge's first path segment for a store's terms (`resolveForgePath`). */
export function forgePathSegment(terms: StorefrontTerminology): string {
  return resolveForgePath(terms.forge.singular).segment;
}

/** A term as it starts a title or a sentence: the first letter upper-cased, the rest as written. */
export function capitalizeTerm(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** A count of a term, singular for one and plural otherwise: "1 card", "12 cards". */
export function termCount(count: number, term: StorefrontTerm): string {
  return `${count} ${count === 1 ? term.singular : term.plural}`;
}

/**
 * The opener's strings that name a term, in the tenant's words: keys of `@loot-os/react`'s
 * `LootLocaleStrings`, for its `locale` prop. Shared by the storefront and the dashboard's box
 * preview, which mounts the same opener. Under the default terminology each equals the opener's
 * own default.
 */
export function resolveOpenerLocale(terms: StorefrontTerminology) {
  const { box, item, battle } = terms;
  return {
    claim: `Claim ${item.plural}`,
    limitedItemNotice: `Limited ${item.singular}. If you cannot withdraw it, you keep it and can sell it for its value.`,
    errorInsufficientBalance: `Not enough balance to open this ${box.singular}.`,
    noOpensYet: `Open the ${box.singular} to record provably-fair rolls here.`,
    battleStartsIn: `${capitalizeTerm(battle.singular)} starts in`,
  };
}
